import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Users, UserPlus, X } from 'lucide-react';
import client from '../../api/client';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle, selectStyle } from '../../styles/tokens';

const ROLE_LABELS = { worker: 'Trabajador', supervisor: 'Supervisor(a)', engineer: 'Ingeniero(a)', manager: 'Gerente' };
const ROLE_COLORS = {
  worker: { bg: '#f1f5f9', color: '#475569' },
  supervisor: { bg: '#dbeafe', color: '#2563eb' },
  engineer: { bg: '#fef3c7', color: '#92400e' },
  manager: { bg: '#ede9fe', color: '#6d28d9' },
};

export default function GangasPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [gangas, setGangas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);

  // New ganga modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGanga, setEditingGanga] = useState(null);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      client.get(`/api/nomina/projects/${projectId}/gangas`),
      client.get('/api/nomina/personas', { params: { active: 'true' } }),
      client.get('/api/projects'),
    ]).then(([gRes, pRes, projRes]) => {
      setGangas(gRes.data);
      setPersonas(pRes.data);
      const proj = projRes.data.find(p => String(p.id) === String(projectId));
      if (proj) setProjectName(proj.name);
    }).catch(console.error).finally(() => setLoading(false));
  }, [projectId]);

  function openCreate() {
    setEditingGanga(null);
    setMembers([{ persona_id: '', role: 'worker' }]);
    setModalOpen(true);
  }

  function openEdit(ganga) {
    setEditingGanga(ganga);
    setMembers(ganga.members.length > 0
      ? ganga.members.map(m => ({ persona_id: String(m.persona_id), role: m.role }))
      : [{ persona_id: '', role: 'worker' }]
    );
    setModalOpen(true);
  }

  function addMemberRow() {
    setMembers(prev => [...prev, { persona_id: '', role: 'worker' }]);
  }

  function updateMember(index, field, value) {
    setMembers(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  }

  function removeMember(index) {
    setMembers(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    const validMembers = members.filter(m => m.persona_id && m.role);
    if (validMembers.length === 0) {
      alert('Agrega al menos un miembro a la ganga.');
      return;
    }
    setSaving(true);
    try {
      if (editingGanga) {
        const res = await client.put(`/api/nomina/gangas/${editingGanga.id}`, { members: validMembers });
        setGangas(prev => prev.map(g => g.id === editingGanga.id ? res.data : g));
      } else {
        const res = await client.post(`/api/nomina/projects/${projectId}/gangas`, { members: validMembers });
        setGangas(prev => [...prev, res.data]);
      }
      setModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar ganga.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(gangaId) {
    if (!confirm('¿Eliminar esta ganga?')) return;
    try {
      await client.delete(`/api/nomina/gangas/${gangaId}`);
      setGangas(prev => prev.filter(g => g.id !== gangaId));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar.');
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      <button onClick={() => navigate('/horas')} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#64748b', fontSize: 13, marginBottom: 14, fontFamily: 'Inter, sans-serif',
      }}>
        <ArrowLeft size={16} /> Volver
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>Gangas</h1>
          {projectName && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Proyecto: {projectName}</div>}
        </div>
        <button onClick={openCreate} style={{
          ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', fontSize: 12,
        }}>
          <Plus size={14} /> Nueva Ganga
        </button>
      </div>

      {/* Ganga List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {gangas.map(g => (
          <div key={g.id} style={{ ...bubbleStyle, padding: '14px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={16} style={{ color: AMBER }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>Ganga #{g.id}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  ({g.members?.length || 0} miembros)
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => openEdit(g)} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                  padding: '4px 10px', fontSize: 11, color: '#475569', cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}>
                  Editar
                </button>
                <button onClick={() => handleDelete(g.id)} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                  padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                }}>
                  <Trash2 size={13} style={{ color: '#ef4444' }} />
                </button>
              </div>
            </div>

            {/* Members */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(g.members || []).map(m => {
                const rc = ROLE_COLORS[m.role] || ROLE_COLORS.worker;
                return (
                  <div key={m.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: rc.bg, borderRadius: 8, padding: '4px 10px',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: NAVY }}>{m.persona_name}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: rc.color,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                    }}>
                      {ROLE_LABELS[m.role] || m.role}
                    </span>
                  </div>
                );
              })}
              {(!g.members || g.members.length === 0) && (
                <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>Sin miembros</span>
              )}
            </div>
          </div>
        ))}
        {gangas.length === 0 && (
          <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            No hay gangas para este proyecto.
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalOpen(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 28px',
            width: '90%', maxWidth: 500, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            maxHeight: '85vh', overflowY: 'auto',
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>
              {editingGanga ? `Editar Ganga #${editingGanga.id}` : 'Nueva Ganga'}
            </h2>

            {/* Member rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {members.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    value={m.persona_id}
                    onChange={e => updateMember(i, 'persona_id', e.target.value)}
                    style={{ ...selectStyle, flex: 2 }}
                  >
                    <option value="">Seleccionar persona...</option>
                    {personas.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.external_id ? ` (${p.external_id})` : ''}
                      </option>
                    ))}
                  </select>
                  <select
                    value={m.role}
                    onChange={e => updateMember(i, 'role', e.target.value)}
                    style={{ ...selectStyle, flex: 1 }}
                  >
                    {Object.entries(ROLE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <button onClick={() => removeMember(i)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    opacity: members.length <= 1 ? 0.3 : 1,
                  }} disabled={members.length <= 1}>
                    <X size={16} style={{ color: '#ef4444' }} />
                  </button>
                </div>
              ))}
            </div>

            <button onClick={addMemberRow} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: `1px dashed ${BORDER_COLOR}`, borderRadius: 8,
              padding: '6px 12px', fontSize: 12, color: '#64748b', cursor: 'pointer',
              fontFamily: 'Inter, sans-serif', width: '100%', justifyContent: 'center',
              marginBottom: 16,
            }}>
              <UserPlus size={14} /> Agregar Miembro
            </button>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalOpen(false)} style={{
                background: 'none', border: `1px solid ${BORDER_COLOR}`,
                borderRadius: 20, padding: '6px 16px', fontSize: 12,
                color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}>
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving} style={{
                ...navyBtn, padding: '6px 16px', fontSize: 12,
                opacity: saving ? 0.6 : 1,
              }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
